import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { register } from '../services/auth';
import { getMembershipTypes, resetOnboarding, checkEmailAvailability } from '../services/api';
import PaymentForm from '../components/membership/PaymentForm';
import GoogleLoginButton from '../components/GoogleLoginButton';
import Alert from '../components/common/Alert';
import { COUNTRIES } from '../constants/countries';

const STORAGE_KEYS = {
  form: 'registerFormData',
  plan: 'registerPlanId',
  step: 'registerStep'
};

const INITIAL_FORM = {
  email: '',
  password1: '',
  password2: '',
  first_name: '',
  last_name: '',
  phone: '',
  address: '',
  city: '',
  state: '',
  postal_code: '',
  country: ''
};

function Register({ setUser }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState(() => {
    const stored = sessionStorage.getItem(STORAGE_KEYS.form);
    if (!stored) return INITIAL_FORM;
    try {
      return { ...INITIAL_FORM, ...JSON.parse(stored) };
    } catch (err) {
      console.warn('Unable to parse stored registration form data:', err);
      return INITIAL_FORM;
    }
  });
  const [step, setStep] = useState(() => {
    // Clear stored state if user is not logged in (fresh visit to register page)
    const hasStoredData = Object.values(STORAGE_KEYS).some(key =>
      sessionStorage.getItem(key)
    );

    if (hasStoredData) {
      console.log('🔐 Register: Clearing stored registration data from previous session');
      Object.values(STORAGE_KEYS).forEach(key => sessionStorage.removeItem(key));
    }

    return 1; // Always start at step 1 for fresh registration
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [membershipTypes, setMembershipTypes] = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState(() => {
    const stored = sessionStorage.getItem(STORAGE_KEYS.plan);
    return stored ? parseInt(stored, 10) : null;
  });
  const [pendingUser, setPendingUser] = useState(null);
  const [pendingMembership, setPendingMembership] = useState(null);

  const selectedPlan = useMemo(
    () => membershipTypes.find((plan) => plan.id === selectedPlanId) || null,
    [membershipTypes, selectedPlanId]
  );

  useEffect(() => {
    sessionStorage.setItem(STORAGE_KEYS.form, JSON.stringify(formData));
  }, [formData]);

  useEffect(() => {
    sessionStorage.setItem(STORAGE_KEYS.step, String(step));
  }, [step]);

  useEffect(() => {
    if (selectedPlanId) {
      sessionStorage.setItem(STORAGE_KEYS.plan, String(selectedPlanId));
    } else {
      sessionStorage.removeItem(STORAGE_KEYS.plan);
    }
  }, [selectedPlanId]);

  useEffect(() => {
    if (step !== 2 || membershipTypes.length > 0 || plansLoading) {
      return;
    }

    const fetchPlans = async () => {
      setPlansLoading(true);
      try {
        const result = await getMembershipTypes();
        const plans = result.results || result;
        setMembershipTypes(plans);
        if (!selectedPlanId && Array.isArray(plans) && plans.length > 0) {
          const communityPlan = plans.find((plan) => plan.price === 0);
          const defaultPlan = communityPlan || plans[0];
          setSelectedPlanId(defaultPlan.id);
        }
      } catch (fetchError) {
        console.error('Failed to load membership plans:', fetchError);
        setError('Unable to load membership plans. Please try again.');
      } finally {
        setPlansLoading(false);
      }
    };

    fetchPlans();
  }, [step, membershipTypes.length, plansLoading, selectedPlanId]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));

    // Clear email error when user starts typing
    if (name === 'email') {
      setEmailError('');
    }
  };

  const validateEmail = async (email) => {
    if (!email || !email.includes('@')) {
      setEmailError('Please enter a valid email address');
      return false;
    }

    setCheckingEmail(true);
    setEmailError('');

    try {
      const response = await checkEmailAvailability(email);
      if (!response.available) {
        setEmailError('A user with this email already exists');
        return false;
      }
      return true;
    } catch (error) {
      setEmailError('Unable to check email availability');
      return false;
    } finally {
      setCheckingEmail(false);
    }
  };

  const handleAccountSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setEmailError('');

    if (formData.password1 !== formData.password2) {
      setError('Passwords do not match');
      return;
    }

    if (!formData.email || !formData.first_name || !formData.last_name) {
      setError('Please complete the required fields.');
      return;
    }

    // Validate email availability
    const isEmailValid = await validateEmail(formData.email);
    if (!isEmailValid) {
      return;
    }

    setStep(2);
  };

  const buildBillingMetadata = () => ({
    address_line1: formData.address,
    city: formData.city,
    state: formData.state,
    postal_code: formData.postal_code,
    country: formData.country,
    phone: formData.phone
  });

  const initialBillingForPayment = useMemo(() => ({
    name: `${formData.first_name} ${formData.last_name}`.trim(),
    email: formData.email,
    address: {
      line1: formData.address,
      city: formData.city,
      state: formData.state,
      postal_code: formData.postal_code,
      country: formData.country || 'US'
    }
  }), [formData]);

  const handlePaymentSuccess = () => {
    setPaymentError('');
    if (pendingUser) {
      setUser(pendingUser);
    }
    setPendingMembership(null);
    setPendingUser(null);
    clearStoredState();

    // Redirect to the originally requested page, or dashboard if none specified
    const redirectTo = searchParams.get('redirect') || '/dashboard';
    navigate(redirectTo, {
      replace: true,
      state: {
        message: `Payment successful! You're now a ${selectedPlan.name}.`
      }
    });
  };

  const handlePaymentFailure = (message) => {
    setPaymentError(message || 'Payment failed. Please try again.');
  };

  const handlePaymentCancel = async () => {
    try {
      await resetOnboarding();
    } catch (err) {
      console.warn('Failed to reset onboarding state:', err);
    }

    setPendingMembership(null);
    setPendingUser(null);
    setPaymentError('');
    setStep(2);
  };

  const clearStoredState = () => {
    Object.values(STORAGE_KEYS).forEach((key) => sessionStorage.removeItem(key));
  };

  const handlePlanSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setPaymentError('');

    if (!selectedPlan) {
      setError('Please select a membership plan to continue.');
      return;
    }

    if (loading || pendingMembership) {
      return;
    }

    setLoading(true);

    try {
      const payload = {
        ...formData,
        membership_type_id: selectedPlan.id,
        billing_metadata: buildBillingMetadata()
      };

      const result = await register(payload);

      if (selectedPlan.price > 0) {
        setPendingUser(result.user);
        setPendingMembership(result.membership);
        setStep(3);
      } else {
        setUser(result.user);
        clearStoredState();

        // Redirect to the originally requested page, or dashboard if none specified
        const redirectTo = searchParams.get('redirect') || '/dashboard';
        navigate(redirectTo, {
          replace: true,
          state: {
            message: `Welcome! You've joined as a ${selectedPlan.name}.`
          }
        });
      }
    } catch (submissionError) {
      console.error('Registration error:', submissionError);
      setError(submissionError.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center space-x-4">
      {[
        { id: 1, label: 'Account' },
        { id: 2, label: 'Plan' },
        { id: 3, label: 'Payment' }
      ].map(({ id, label }) => (
        <div key={id} className="flex items-center space-x-2">
          <div
            className={`h-3 w-3 rounded-full ${
              step >= id ? 'bg-blue-600' : 'bg-gray-300'
            }`}
          />
          <span className="text-sm text-gray-600">{label}</span>
        </div>
      ))}
    </div>
  );

  const renderAccountForm = () => (
    <form className="mt-8 space-y-6" onSubmit={handleAccountSubmit}>
      <div className="grid grid-cols-1 gap-4 mb-4">
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700">
            Email address *
          </label>
          <div className="mt-1 relative">
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              className={`block w-full py-2.5 px-3 shadow-sm sm:text-sm rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                emailError
                  ? 'border-red-300 text-red-900 placeholder-red-300'
                  : 'border-gray-300'
              }`}
              value={formData.email}
              onChange={handleChange}
              placeholder="your.email@domain.com"
            />
            {checkingEmail && (
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                <svg className="animate-spin h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
            )}
          </div>
          {emailError && (
            <p className="mt-1 text-sm text-red-600">{emailError}</p>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="password1" className="block text-sm font-medium text-gray-700">
              Password *
            </label>
            <input
              id="password1"
              name="password1"
              type="password"
              required
              className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full py-2.5 px-3 shadow-sm sm:text-sm border-gray-300 rounded-md"
              value={formData.password1}
              onChange={handleChange}
            />
          </div>
          <div>
            <label htmlFor="password2" className="block text-sm font-medium text-gray-700">
              Confirm Password *
            </label>
            <input
              id="password2"
              name="password2"
              type="password"
              required
              className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full py-2.5 px-3 shadow-sm sm:text-sm border-gray-300 rounded-md"
              value={formData.password2}
              onChange={handleChange}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="first_name" className="block text-sm font-medium text-gray-700">
              First name *
            </label>
            <input
              id="first_name"
              name="first_name"
              type="text"
              required
              className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full py-2.5 px-3 shadow-sm sm:text-sm border-gray-300 rounded-md"
              value={formData.first_name}
              onChange={handleChange}
            />
          </div>
          <div>
            <label htmlFor="last_name" className="block text-sm font-medium text-gray-700">
              Last name *
            </label>
            <input
              id="last_name"
              name="last_name"
              type="text"
              required
              className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full py-2.5 px-3 shadow-sm sm:text-sm border-gray-300 rounded-md"
              value={formData.last_name}
              onChange={handleChange}
            />
          </div>
        </div>

        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
            Phone
          </label>
          <input
            id="phone"
            name="phone"
            type="tel"
            className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full py-2.5 px-3 shadow-sm sm:text-sm border-gray-300 rounded-md"
            value={formData.phone}
            onChange={handleChange}
          />
        </div>

        <div>
          <label htmlFor="address" className="block text-sm font-medium text-gray-700">
            Address
          </label>
          <input
            id="address"
            name="address"
            type="text"
            className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full py-2.5 px-3 shadow-sm sm:text-sm border-gray-300 rounded-md"
            value={formData.address}
            onChange={handleChange}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label htmlFor="city" className="block text-sm font-medium text-gray-700">
              City
            </label>
            <input
              id="city"
              name="city"
              type="text"
              className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full py-2.5 px-3 shadow-sm sm:text-sm border-gray-300 rounded-md"
              value={formData.city}
              onChange={handleChange}
            />
          </div>
          <div>
            <label htmlFor="state" className="block text-sm font-medium text-gray-700">
              State
            </label>
            <input
              id="state"
              name="state"
              type="text"
              className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full py-2.5 px-3 shadow-sm sm:text-sm border-gray-300 rounded-md"
              value={formData.state}
              onChange={handleChange}
            />
          </div>
          <div>
            <label htmlFor="postal_code" className="block text-sm font-medium text-gray-700">
              ZIP / Postal code
            </label>
            <input
              id="postal_code"
              name="postal_code"
              type="text"
              className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full py-2.5 px-3 shadow-sm sm:text-sm border-gray-300 rounded-md"
              value={formData.postal_code}
              onChange={handleChange}
            />
          </div>
          <div>
            <label htmlFor="country" className="block text-sm font-medium text-gray-700">
              Country
            </label>
            <select
              id="country"
              name="country"
              className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full py-2.5 px-3 shadow-sm sm:text-sm border-gray-300 rounded-md"
              value={formData.country}
              onChange={handleChange}
            >
              <option value="">Select a country</option>
              {COUNTRIES.map((country) => (
                <option key={country.code} value={country.code}>
                  {country.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="flex justify-between items-center">
        <button
          type="button"
          className="text-sm text-blue-600 hover:text-blue-500"
          onClick={() => {
            const redirectTo = searchParams.get('redirect');
            const loginUrl = redirectTo ? `/login?redirect=${encodeURIComponent(redirectTo)}` : '/login';
            navigate(loginUrl);
          }}
        >
          Already have an account?
        </button>

        <button
          type="submit"
          disabled={checkingEmail}
          className="px-6 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-300 disabled:cursor-not-allowed"
        >
          {checkingEmail ? 'Checking email...' : 'Continue to Plan'}
        </button>
      </div>
    </form>
  );

  const renderPlanSelection = () => (
    <form className="mt-8 space-y-6" onSubmit={handlePlanSubmit}>
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Choose your membership</h3>
        <p className="text-sm text-gray-600 mb-6">
          Select the plan that works best for you. Paid plans will prompt for payment after account creation.
        </p>

        {plansLoading && (
          <div className="text-center text-gray-500">Loading plans...</div>
        )}

        {!plansLoading && membershipTypes.length > 0 && (
          <div className="grid grid-cols-1 gap-4">
            {membershipTypes.map((plan) => {
              const isSelected = plan.id === selectedPlanId;
              return (
                <label
                  key={plan.id}
                  className={`border rounded-lg p-4 cursor-pointer transition shadow-sm ${
                    isSelected ? 'border-blue-600 bg-blue-50' : 'border-gray-200 hover:border-blue-300'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center space-x-3">
                        <input
                          type="radio"
                          name="membership_plan"
                          value={plan.id}
                          checked={isSelected}
                          onChange={() => setSelectedPlanId(plan.id)}
                          className="mt-1"
                        />
                        <div>
                          <h4 className="text-lg font-medium text-gray-900">{plan.name}</h4>
                          <p className="text-sm text-gray-600 mt-1">{plan.description}</p>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="text-xl font-semibold text-gray-900">
                        {plan.price === 0 ? 'Free' : `$${Number(plan.price).toFixed(2)}`}
                      </span>
                      {plan.duration_months ? (
                        <p className="text-xs text-gray-500">{plan.duration_months}-month membership</p>
                      ) : null}
                    </div>
                  </div>

                  {plan.benefits && Array.isArray(plan.benefits.highlights) && plan.benefits.highlights.length > 0 && (
                    <ul className="mt-3 list-disc list-inside text-sm text-gray-600">
                      {plan.benefits.highlights.map((benefit, index) => (
                        <li key={index}>{benefit}</li>
                      ))}
                    </ul>
                  )}
                </label>
              );
            })}
          </div>
        )}
      </div>

      <div className="flex justify-between">
        <button
          type="button"
          onClick={() => setStep(1)}
          className="px-6 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          Back
        </button>
        <button
          type="submit"
          disabled={loading}
          className="px-6 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-300"
        >
          {loading ? 'Creating account...' : selectedPlan && selectedPlan.price > 0 ? 'Continue to Payment' : 'Join for Free'}
        </button>
      </div>
    </form>
  );

  const renderPaymentStep = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Complete your payment</h3>
        <p className="text-sm text-gray-600">
          Provide payment details to activate the {selectedPlan?.name} plan.
        </p>
      </div>

      {paymentError && (
        <Alert type="error" message={paymentError} onClose={() => setPaymentError('')} />
      )}

      {pendingMembership && selectedPlan ? (
        <PaymentForm
          plan={selectedPlan}
          currentMembership={pendingMembership}
          onSuccess={handlePaymentSuccess}
          onError={setPaymentError}
          onFailure={handlePaymentFailure}
          onCancel={handlePaymentCancel}
          autoNavigateOnSuccess={false}
          initialBilling={initialBillingForPayment}
        />
      ) : (
        <div className="text-sm text-gray-600">Preparing payment details...</div>
      )}
    </div>
  );

  return (
    <div className="min-h-full flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Create your account
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Or{' '}
            <Link
              to={searchParams.get('redirect') ? `/login?redirect=${encodeURIComponent(searchParams.get('redirect'))}` : '/login'}
              className="font-medium text-blue-600 hover:text-blue-500"
            >
              sign in to your existing account
            </Link>
          </p>
        </div>

        {renderStepIndicator()}

        {error && (
          <Alert type="error" message={error} onClose={() => setError('')} />
        )}

        {step === 1 && renderAccountForm()}
        {step === 2 && renderPlanSelection()}
        {step === 3 && renderPaymentStep()}

        {step === 1 && (
          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">Or sign up with</span>
              </div>
            </div>

            <div className="mt-6">
              <GoogleLoginButton redirectTo={searchParams.get('redirect')} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Register;
